import { types, Instance, isAlive, IAnyStateTreeNode } from 'mobx-state-tree';
import moment, { Moment } from 'moment';
import getFormattedTime from '../../../helpers/getFormattedTime';
import getShiftEndMoment from '../../../helpers/getShiftEndMoment';
import {
  MAX_OFFLINE_MINUTES,
  defaultDateTimeFormat,
} from '../../../constants';
import { DistanceUnit } from './DistanceUnitEnum';
import { RouteDtoModelBase } from './RouteDtoModel.base';
import getAppRoot from '../utils/getAppRoot';
import convertRoutePolyline from '../../../helpers/routes';
import getScaledColor from '../../../helpers/getScaledColor';
import { routeLateEvent } from '../../../helpers/lateness';
import { IColor } from '../../../types/IColor';
import { DecodedPolyline } from '../../../helpers/routes/convertRoutePolyline';
import { RouteTimelineDtoModelType } from './RouteTimelineDtoModel';
import { IRouteLateEvent } from '../../../types/TRoute';
import { RouteConstraintLocationDtoModelType } from './RouteConstraintLocationDtoModel';
import { MapMarkerSnapshot } from './MapMarkerModel';
import { RouteDriverLocation } from '../DriverLocation';
import { RouteSolutionDtoModelType, SolutionStatusOnlyDtoModelType } from '.';

// Detached routes (mid-delete / preview eviction) are their own MST root, so
// getAppRoot returns the route itself — which has no `ui`.
const safeAppUi = (self: IAnyStateTreeNode) => {
  if (!isAlive(self)) return undefined;
  return getAppRoot(self)?.ui;
};

/* The TypeScript type of an instance of RouteDtoModel */
export interface RouteDtoModelType extends Instance<typeof RouteDtoModel.Type> {
}

/* A graphql query fragment builders for RouteDtoModel */
export { selectFromRouteDto, routeDtoModelPrimitives, RouteDtoModelSelector } from './RouteDtoModel.base';

/* Marker types are defined in ./MapMarkerModel; re-exported here so they remain reachable via the
   generated models barrel (which can't be edited to export the hand-written MapMarkerModel directly). */
export type {
  MapMarkerModelType, MapMarkerSnapshot, MarkerClusterFeature, MapMarkerType,
} from './MapMarkerModel';

export type RouteStatus = 'planning' | 'published' | 'started' | 'finished';

export const ROUTE_STATUS_SORT_ORDER: Record<RouteStatus, number> = {
  planning: 0,
  published: 1,
  started: 2,
  finished: 3,
};

/**
 * RouteDtoModel
 */
export const RouteDtoModel = RouteDtoModelBase
  .props({
    isLoaded: types.optional(types.boolean, false),
  })
  .views((self) => ({
    // `activeRouteSolution` / `publishedRouteSolution` are MSTGQLRefs to RouteSolutionDto. When the
    // day's routes are cleared and refetched (e.g. changing the date in the scheduling preview), a
    // route can briefly outlive the solution it points at, so reading the ref throws
    // "Failed to resolve reference … to RouteSolutionDtoModel". `isAlive(self)` only proves the route
    // node is attached — it does NOT prove the ref resolves — so resolve defensively: a detached route
    // or a dangling ref reads as "no solution" until the store repopulates (ROUT-2850).
    get resolvedActiveRouteSolution(): RouteSolutionDtoModelType | undefined {
      if (!isAlive(self)) return undefined;
      try {
        return self.activeRouteSolution ?? undefined;
      } catch {
        return undefined;
      }
    },
    get resolvedPublishedRouteSolution(): RouteSolutionDtoModelType | undefined {
      if (!isAlive(self)) return undefined;
      try {
        return self.publishedRouteSolution ?? undefined;
      } catch {
        return undefined;
      }
    },
  }))
  .views((self) => ({
    get routeSolution(): RouteSolutionDtoModelType {
      return (safeAppUi(self)?.showPublishedRoutes
        ? self.resolvedPublishedRouteSolution
        : self.resolvedActiveRouteSolution) as RouteSolutionDtoModelType;
    },
    get timeline(): RouteTimelineDtoModelType[] {
      // This should be activeRouteSolution for now since we do not query for published route timeline events
      return (self.resolvedActiveRouteSolution?.routeTimeline || []).filter(event => (
        !event.isStopEvent || event.orders == null || event.orders.length > 0
      ));
    },
  }))
  .views((self) => ({
    get tagNames(): string[] {
      return self.tags?.map((tag: { name: any }) => tag.name).filter((name: any): name is string => !!name) ?? [];
    },
    get totalLoad(): number {
      return self.routeSolution?.load || 0;
    },
    get workingTimeMinutes(): number {
      return self.routeSolution?.workingTimeMinutes || 0;
    },
    get totalCapacity(): number {
      return self.constraints?.capacity || 0;
    },
    get finished(): boolean {
      return !!self.routeSolution?.finished;
    },
  }))
  .views((self) => ({
    get hasPublishablePendingChanges(): boolean {
      // Do not use `finished` here. That view follows the published/active toggle, and the
      // nav badge query only loads `activeRouteSolution.finished`. A finished route would
      // stay publishable whenever the other solution is the one on screen.
      return !!self.unpublishedChanges
        && !self.resolvedActiveRouteSolution?.finished
        && !self.resolvedPublishedRouteSolution?.finished;
    },
    get status(): RouteStatus {
      if (self.finished) return 'finished';
      if (self.isStarted) return 'started';
      if (self.isDispatched) return 'published';
      return 'planning';
    },
    get isOverCapacity(): boolean {
      return self.routeSolution?.isOverCapacity;
    },
    get isOvertime(): boolean {
      return self.routeSolution?.isOvertime;
    },
    get totalStopsCount(): number {
      return self.routeSolution?.stopsCount || 0;
    },
    get totalOrdersCount(): number {
      return self.routeSolution?.ordersCount || 0;
    },
    get totalDistance(): number {
      if (!isAlive(self)) return 0;
      const distance = getAppRoot(self).organization.settings.distanceUnit === DistanceUnit.KM ? self.routeSolution?.kmsFromDistance
        || 0 : self.routeSolution?.milesFromDistance || 0;
      return Math.round(distance * 10) / 10;
    },
    get distanceUnit(): DistanceUnit {
      if (!isAlive(self)) return DistanceUnit.KM;
      return getAppRoot(self).organization.settings.distanceUnit as DistanceUnit;
    },
    get isHovered(): boolean {
      return safeAppUi(self)?.hoveredRoute?.id === self.id;
    },
    get lastDriverLocation(): RouteDriverLocation | undefined {
      return getAppRoot(self).driverLocation?.routeDriverLocations?.get(self.id);
    },
    get lastLocationUpdateMinutes(): number | undefined {
      return this.lastDriverLocation?.timeSinceLastUpdateMinutes;
    },
    get isOnline(): boolean | undefined {
      if (self.finished) return undefined;
      const lastUpdate = this.lastLocationUpdateMinutes;
      return lastUpdate !== undefined ? lastUpdate <= MAX_OFFLINE_MINUTES : undefined;
    },
    get startEvent(): RouteTimelineDtoModelType | undefined {
      return self.timeline?.find((e) => e.eventType === 'leaving_start_location');
    },
    get endEvent(): RouteTimelineDtoModelType | undefined {
      return self.timeline?.find((e) => e.eventType === 'reaching_end_location');
    },
    get firstStopEvent(): RouteTimelineDtoModelType | undefined {
      return self.timeline?.find((e) => e.eventType === 'delivering_stop');
    },
    get lastStopEvent(): RouteTimelineDtoModelType | undefined {
      const stopEvents = self.timeline?.filter((e) => e.eventType === 'delivering_stop');

      return stopEvents?.length ? stopEvents[stopEvents.length - 1] : undefined;
    },
    get startLocation(): RouteConstraintLocationDtoModelType {
      return self.constraints?.startLocation;
    },
    get endLocation(): RouteConstraintLocationDtoModelType | undefined {
      return self.constraints?.endLocation;
    },
    get lateEvent(): IRouteLateEvent | undefined {
      return routeLateEvent(self.routePerformanceEvent);
    },
    get polylineArray(): DecodedPolyline {
      if (!self.routeSolution?.polyline) {
        return [];
      }
      return convertRoutePolyline(self.routeSolution?.polyline, 6);
    },
    get remainingWorkingTime(): number | undefined {
      const durationMinutes = self.constraints?.durationMinutes;
      const { workingTimeMinutes } = self;

      if (workingTimeMinutes !== undefined && durationMinutes) {
        // Minimum value to display for remaining is 0
        return Math.max(0, durationMinutes - workingTimeMinutes);
      }
      return undefined;
    },
    get percentWorkingTime(): number | undefined {
      const durationMinutes = self.constraints?.durationMinutes;
      const { workingTimeMinutes } = self;

      if (workingTimeMinutes && durationMinutes) {
        /**
         * Values smaller than 1% would otherwise be hidden in visualizations
         * so 1 will be the smallest value available.
         */
        return Math.ceil((workingTimeMinutes / durationMinutes) * 100);
      }
      return undefined;
    },
    get remainingCapacity(): number | undefined {
      const { totalLoad, totalCapacity } = self;

      if (totalLoad !== undefined && totalCapacity) {
        // Minimum value to display for remaining is 0
        return Math.max(0, totalCapacity - totalLoad);
      }
      return undefined;
    },
    get percentLoad(): number | undefined {
      const { totalLoad, totalCapacity } = self;
      if (totalLoad && totalCapacity) {
        /**
         * Values smaller than 1% would otherwise be hidden in visualizations
         * so 1 will be the smallest value available.
         */
        return Math.ceil((totalLoad / totalCapacity) * 100);
      }
      return undefined;
    },
    get percentComplete(): number {
      const completedStopsCount = self.completedStopsCount || 0;
      const totalVisits = this.totalStopsCount || 0;

      return (completedStopsCount / totalVisits) * 100;
    },
    get percentProgress(): number {
      const completedStopsCount = self.completedStopsCount || 0;
      const totalVisits = this.totalStopsCount || 0;

      return ((completedStopsCount) / totalVisits) * 100;
    },
    get totalRemainingVisits(): number | undefined {
      // const { totalRemaining } = getAppRoot(self).routeTotals();

      const completedStopsCount = self.completedStopsCount || 0;
      const totalStopsCount = this.totalStopsCount || 0;
      const totalRemaining = totalStopsCount - completedStopsCount;

      return totalRemaining >= 0 ? totalRemaining : undefined;
    },
    get color(): IColor {
      const { hexColor } = self;
      // Control to avoid error when hexColor is undefined
      return hexColor ? getScaledColor(hexColor) : getScaledColor('#000000');
    },
    // Check whether current route has a stop that is being unscheduled, returning the stop ID.
    get currentUnschedulingStop(): number | null | undefined {
      return Array.from<SolutionStatusOnlyDtoModelType>(getAppRoot(self).model.solutionStatusOnlyDtos.values()).find((s) => s.actionType === 'unassign'
        && s.assignAction.targetRouteId != null
        && String(s.assignAction.targetRouteId) === self.id)?.assignAction.visitId;
    },
    // Check whether current route is involved in a pending optimize assign action where
    // it is either the origin or target route.
    get isAssigningStop(): boolean {
      return !!Array.from<SolutionStatusOnlyDtoModelType>(getAppRoot(self).model.solutionStatusOnlyDtos.values()).find((s) => s.actionType === 'optimize_assign'
        && ((s.assignAction && s.assignAction.targetRouteId != null && String(s.assignAction.targetRouteId) === self.id)
          || (s.assignAction && s.assignAction.originRouteId != null && String(s.assignAction.originRouteId) === self.id)));
    },
    // Check whether current route has a stop that has been edited and is therefore having its ETAs rec-calculated
    get isEditingStop(): boolean {
      return !!Array.from<SolutionStatusOnlyDtoModelType>(getAppRoot(self).model.solutionStatusOnlyDtos.values()).find((s) => s.actionType === 'edit_assign'
        && s.assignAction.targetRouteId != null
        && ((s.assignAction.targetRouteId != null && String(s.assignAction.targetRouteId) === self.id)));
    },
    get isMovingStop(): boolean {
      return !!Array.from<SolutionStatusOnlyDtoModelType>(getAppRoot(self).model.solutionStatusOnlyDtos.values()).find((s) => s.actionType === 'assign_sequence'
        && s.assignAction.targetRouteId != null
        && ((s.assignAction.targetRouteId != null && String(s.assignAction.targetRouteId) === self.id)));
    },
    get isBeingModified(): boolean {
      // Guard against accessing detached nodes during date changes when routes are cleared from the tree
      if (!isAlive(self)) return false;
      return (!!self.timeline?.some((e) => e.orders?.[0]?.isBeingModified) || !!self.isScheduling || !!self.isUnscheduling || !!self.isLocked || !!self.isReversing) || false;
    },
    get isBeingRescheduled(): boolean {
      return !!this.currentUnschedulingStop || this.isAssigningStop || this.isEditingStop || this.isMovingStop;
    },
    get shiftStartTime(): Moment | undefined {
      return self.startDate ? moment(`${self.startDate} ${self?.constraints?.startTime}`, defaultDateTimeFormat) : undefined;
    },
    get shiftEndTime(): Moment | undefined {
      return self.startDate
        ? getShiftEndMoment(self.startDate, self?.constraints?.startTime, self?.constraints?.endTime)
        : undefined;
    },
    // Planned departure from the start location, as epoch ms. Mirrors the Gantt route-line start
    // (routeGanttFormats) so a departure-time sort agrees with the drawn bar. Shift start is
    // deliberately not a fallback: a route with no solution has no departure.
    // A number rather than a Moment so MobX's default equality short-circuits: a solution refresh
    // that leaves the departure unchanged doesn't invalidate the timeline's sort computeds.
    get plannedDepartureTime(): number | undefined {
      const departure = this.startEvent?.departureTime || this.firstStopEvent?.startTime;
      return departure ? moment(departure, defaultDateTimeFormat).valueOf() : undefined;
    },
    get shiftStartTimeString(): string | undefined {
      return getFormattedTime(this.shiftStartTime, getAppRoot(self).organization.settings?.dateTimeFormat);
    },
    get shiftEndTimeString(): string | undefined {
      return getFormattedTime(this.shiftEndTime, getAppRoot(self).organization.settings?.dateTimeFormat);
    },
  }))
  .views((self) => ({
    // These marker views return plain snapshots rather than MST nodes: the scheduling map only
    // reads them as data (Object.values(...) / array spreads fed into GeoJSON.parse, plus existence
    // checks), so building an MST node per stop is wasted work on a hot path.
    get visits(): { [visitId: string]: MapMarkerSnapshot } | undefined {
      return self.timeline?.reduce((acc, event) => {
        if (event.orders?.[0]?.id != null && event.orders?.[0]?.activeLocation) {
          acc[event.orders[0].id] = {
            id: event.orders[0].id,
            type: event.eventType,
            routeId: self.id,
            routeUuid: self.uuid,
            latitude: event.orders[0].activeLocation.latitude || 0,
            longitude: event.orders[0].activeLocation.longitude || 0,
            color: self.color,
            pendingColor: event.orders[0].isBeingModified && event.orders[0].pendingColor ? event.orders[0].pendingColor : undefined,
            sequence: event.stopSequence || 0,
            status: event.currentStatus?.status,
            isPending: event.orders[0].isBeingModified,
          };
        }

        return acc;
      }, {} as { [visitId: string]: MapMarkerSnapshot });
    },
    get stops(): { [visitId: string]: MapMarkerSnapshot } | undefined {
      return self.timeline?.reduce((acc, event) => {
        if (event.isStopEvent) {
          acc[event.id] = {
            id: event.id,
            type: event.eventType,
            routeId: self.id,
            routeUuid: self.uuid,
            // We'll use the timeline constraints lat/long used in the engine request
            latitude: event.constraints?.latitude || (event.orders?.[0]?.activeLocation?.latitude || 0),
            longitude: event.constraints?.longitude || (event.orders?.[0]?.activeLocation?.longitude || 0),
            color: self.color,
            pendingColor: event.orders?.[0]?.isBeingModified && event.orders?.[0]?.pendingColor ? event.orders?.[0]?.pendingColor : undefined,
            sequence: event.stopSequence || undefined,
            status: event.currentStatus?.status,
            isPending: event.orders?.[0]?.isBeingModified,
          };
        }

        return acc;
      }, {} as { [visitId: string]: MapMarkerSnapshot });
    },
    get startMarker(): MapMarkerSnapshot | null {
      return self.startEvent ? {
        id: self.startEvent.id,
        type: self.startEvent?.eventType,
        routeId: self.id,
        routeUuid: self.uuid,
        latitude: self.startLocation?.latitude || 0,
        longitude: self.startLocation?.longitude || 0,
        color: self.color,
        sequence: self.startEvent.sequence || 0,
        status: self.startEvent?.isDeparted ? self.startEvent?.currentStatus?.status : undefined,
      } : null;
    },
    get endMarker(): MapMarkerSnapshot | null {
      return self.endEvent ? {
        id: self.endEvent.id,
        type: self.endEvent?.eventType,
        routeId: self.id,
        routeUuid: self.uuid,
        latitude: self.endLocation?.latitude || 0,
        longitude: self.endLocation?.longitude || 0,
        color: self.color,
        sequence: self.endEvent.sequence || 0,
        status: self.endEvent?.isDeparted ? self.endEvent?.currentStatus?.status : undefined,
      } : null;
    },
    get isSelected(): boolean {
      const ui = safeAppUi(self);
      return !!ui?.selectedRoutesBulk?.has(self.uuid!) || ui?.selectedRoute?.id === self.id;
    },
  }))
  .actions((self) => ({
    setIsDeleting(isDeleting: boolean) {
      self.isDeleting = isDeleting;
    },
    setIsScheduling(isScheduling: boolean) {
      self.isScheduling = isScheduling;
    },
    setIsReversing(isReversing: boolean) {
      self.isReversing = isReversing;
    },
    setHexColor(hexColor: string) {
      self.hexColor = hexColor;
    },
    clearLastLocation() {
      self.lastLocation = null;
    },
  }));
